'use client';

import type { ReactNode } from 'react';
import useMap from '@/hooks/useMap';
import { getRightContainerStyles } from '@/utils/calcHeaderHeight';
import { useCallback, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from '@/lib/router-events/patch-router/router';
import Button from '@/components/shared/button/Button';
import Icon from '@/components/shared/Icons';
import { prepareUrl } from '@/utils/urlHelper';
import { queryStringConstants, VIEW } from '@/constants/queryConstants';

type Props = {
  children: ReactNode;
  isNotificationBannerVisible: boolean;
  isLoggedIn: boolean;
  listViewText: string;
  totalVenues: number;
};

function getStyles(
  isNotificationBannerVisible: boolean,
  isLoggedIn: boolean,
  mapExpanded: boolean,
  view?: string | null
): string {
  const styles = getRightContainerStyles(isLoggedIn, isNotificationBannerVisible);
  const fullMap = view === VIEW.FULL_MAP;
  const map = view === VIEW.MAP;
  return mapExpanded
    ? `sticky top-[${styles.mapExpanded.mobileTop}rem] xl:top-[${styles.mapExpanded.top}rem] h-[calc(100vh_-_${
        styles.mapExpanded.top
      }rem)] overflow-auto w-2/5 grow-[2] ${fullMap ? 'hidden xl:h-[880px] xl:block' : ''} ${
        map ? '' : 'hidden xl:block'
      }`
    : `sticky top-[${styles.mapCollapsed.mobileTop}rem] xl:top-[${styles.mapCollapsed.top}rem] h-[calc(100vh_-_${
        styles.mapCollapsed.height
      }rem)] overflow-auto lg:rounded-xl ${fullMap ? 'hidden xl:h-[880px] xl:block' : 'xl:h-full'} ${
        map || fullMap ? 'w-full xl:pb-0' : 'xl hidden xl:pb-24 xl:block w-1/3'
      } ${map ? 'xl:w-1/4' : ''}`;
}

export default function RightContentContainer({
  children,
  isNotificationBannerVisible,
  isLoggedIn,
  listViewText,
  totalVenues
}: Props): JSX.Element {
  const { mapExpanded } = useMap();
  const searchParams = useSearchParams();
  const view = useMemo(() => searchParams?.get(queryStringConstants.VIEW), [searchParams]);
  const router = useRouter();
  const pathname = usePathname();
  const onClick = useCallback(() => {
    router.push(prepareUrl(pathname, searchParams, { key: queryStringConstants.VIEW, value: VIEW.LIST }));
  }, [pathname, router, searchParams]);

  const styles = useMemo((): { container: string; button: string } => {
    const container = `${getStyles(isNotificationBannerVisible, isLoggedIn, mapExpanded, view)} no-scrollbar`;
    const button = `fixed bottom-9 left-[calc(50%_-_4.5rem)] z-10 ${
      view === VIEW.MAP || view === VIEW.FULL_MAP ? 'block' : 'hidden'
    } lg:hidden`;
    return { container, button };
  }, [isNotificationBannerVisible, isLoggedIn, mapExpanded, view]);

  return (
    <div className={styles.container} id="right">
      {totalVenues > 0 && (
        <div className={styles.button}>
          <Button
            aria-label={listViewText}
            appearance="filled"
            className="rounded-full"
            icon={<Icon id="list" className="h-4 w-4 fill-background" />}
            text={listViewText}
            onClick={onClick}
          />
        </div>
      )}
      {children}
    </div>
  );
}
